import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { FaLink } from 'react-icons/fa';
import { toastSuccess, toastError } from '../utils/toasts';
import {
  RootState,
  setOrderItemData,
  setTicketsSelected,
  useCreateOrderMutation,
  useGetAllTicketsQuery,
  useGetOneCashierReportQuery,
  useGetEventCashRegisterByIdQuery,
  useGetBusinessCashRegisterByIdQuery,
  useCloseEventCashRegisterMutation,
  useGetEventCashRegisterMovementsQuery,
} from '../store';
import Button from '../components/atoms/Button';
import TicketDTO from '../models/ticket';
import OrdersTableSkeleton from '../components/OrdersTableSkeleton';
import useNavigateWithQuery from '../hooks/useNavigateWithQuery';
import useGetSelectedEvent from '../hooks/useGetSelectedEvent';
import useGetUserRole from '../hooks/useGetUserRole';
import IconInfoCard from '../components/atoms/IconInfoCard';
import { OrderItemData } from '../models/order';
import SelectTicketsTable, { TicketSelection } from '../components/SelectTicketsTable';
import LoadingLottie from '../components/atoms/LoadingLottie';
import AdminSectionTitle from '../components/AdminSectionTitle';
// import MenuDropdown from '../components/atoms/MenuDropdown';
import Summary from '../components/Summary';
import CustomModal from '../components/atoms/CustomModal';
import CreateEditInPerson from '../components/CreateEditInPerson';
import {
  PaymentMethod, PAYMENT_METHOD_LABELS, getPaymentMethodFromLabel, PaymentProvider,
} from '../models/payment';
import { formatCurrency } from '../utils/shared';
// import TransactionsTable from '../components/TransactionsTable';
import AddUserExpense from '../components/AddUserExpense';
import { EventTransactionType } from '../models/event-transaction';
import Select from '../components/atoms/Select/Select';
import NumeratedSeats from '../components/NumeratedSeats';
import { DisplaySeatDTO } from '../models/seat';
import HorizontalTabs from '../components/atoms/HorizontalTabs';
import CashMovementTable from '../components/CashMovementTable';
import Input from '../components/atoms/Input';
import useGetSelectedBusiness from '../hooks/useGetSelectedBusiness';
import GotCoupon from '../components/GotCoupon';
import CouponDTO from '../types/Coupon';

interface Props {
  view?: boolean;
}

const InPersonSalePage: React.FC<Props> = ({ view = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigateWithQuery();
  const location = useLocation();
  const { cashier_id: cashierId, session_id: sessionId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isBackoffice = location.pathname.includes('admin');
  const role = useGetUserRole({ skip: !isBackoffice });
  const { event, eventLoading } = useGetSelectedEvent({ slug: false });
  const { data: eventTickets, isLoading: ticketsLoading } = useGetAllTicketsQuery(
    { businessId: event?.business_id || '', eventId: event?.id || '', publicSaleAvailable: false },
    { skip: !event },
  );
  const reservation = useSelector((state: RootState) => state.orders.reservation);
  const { data: cashRegister } = useGetEventCashRegisterByIdQuery({
    businessId: event?.business_id || '',
    eventId: event?.id || '',
    cashRegisterId: cashierId || '',
  }, { skip: !event || !cashierId });
  const { data: businessCashRegister } = useGetBusinessCashRegisterByIdQuery({
    businessId: event?.business_id || '',
    cashRegisterId: cashRegister?.cash_register_id || '',
  }, { skip: !event || !cashRegister });
  const [allTickets, setAllTickets] = useState<TicketDTO[]>([]);
  const [entryTickets, setEntryTickets] = useState<TicketDTO[]>([]);
  const [extraTickets, setExtraTickets] = useState<TicketDTO[]>([]);
  const [ticketSelections, setTicketSelections] = useState<TicketSelection[]>([]);
  const [selectedSeats, setSelectedSeats] = useState<DisplaySeatDTO[]>([]);
  const [createOrder, resultsCreate] = useCreateOrderMutation();
  const [totalAmount, setTotalAmount] = useState(0);
  const [selectedTab, setSelectedTab] = useState<'sale' | 'report'>(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl === 'sale' || tabFromUrl === 'report') {
      return tabFromUrl;
    }
    return ((cashRegister?.status === 'open') && !view) ? 'sale' : 'report';
  });
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod>(() => {
    const savedMethod = localStorage.getItem('selectedPaymentMethod');
    if (savedMethod) {
      const method = getPaymentMethodFromLabel(savedMethod);
      if (method !== undefined) {
        return method;
      }
    }
    return PaymentMethod.WIRE_TRANSFER;
  });
  const [showModal, setShowModal] = useState(false);
  const [closeEventCashRegister] = useCloseEventCashRegisterMutation();
  const [modalType, setModalType] = useState<'edit' | 'delete' | 'expense' | 'close' | null>(null);
  const [closingAmount, setClosingAmount] = useState(0);
  const [appliedCoupon, setAppliedCoupon] = useState<CouponDTO | null>(null);
  const { data: cashierReport } = useGetOneCashierReportQuery({
    businessId: event?.business_id || '',
    eventId: event?.id || '',
    cashierId: cashierId || '',
  }, { skip: !event || !cashierId, refetchOnFocus: true });
  const [expenseType, setExpenseType] = useState<EventTransactionType>(EventTransactionType.CashOut);
  const { data: movements } = useGetEventCashRegisterMovementsQuery({
    businessId: event?.business_id || '',
    eventId: event?.id || '',
    cashRegisterId: cashierId || '',
    sessionId: sessionId || '',
  }, { skip: !event || !cashierId, refetchOnFocus: true });
  const { business, businessLoading } = useGetSelectedBusiness();

  useEffect(() => {
    localStorage.setItem('selectedPaymentMethod', PAYMENT_METHOD_LABELS[selectedPaymentMethod]);
  }, [selectedPaymentMethod]);

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl === 'sale' || tabFromUrl === 'report') {
      return;
    }
    if (cashRegister?.status === 'close' || view) {
      setSelectedTab('report');
    } else {
      setSelectedTab('sale');
    }
  }, [cashRegister]);

  useEffect(() => {
    if (eventTickets) {
      const filtered = eventTickets
        ?.filter((t) => (isBackoffice ? t : t.active))
        ?.filter((t) => ((role === 'ADMIN' || role === 'ROOT') && isBackoffice
          ? t
          : !t.is_only_admin)) || [];
      setAllTickets(filtered);
    }
  }, [eventTickets, isBackoffice, role]);

  useEffect(() => {
    setEntryTickets(allTickets.filter((t) => !t.is_extra));
    setExtraTickets(allTickets.filter((t) => t.is_extra));
  }, [allTickets]);

  useEffect(() => {
    if (event?.has_numerated_seats && event.tickets) {
      const calculatedTickets = selectedSeats.map((s): TicketSelection | null => {
        const ticket = event.tickets.find((t) => t.id === s.ticket_id);
        if (!ticket) {
          console.error(`Ticket not found for seat ID: ${s.id}, ticket ID: ${s.ticket_id}`);
          return null;
        }
        const fullTicket = allTickets.find((t) => t.id === ticket.id) || ticket;
        return {
          ticket: fullTicket,
          quantity: 1,
          seat_name: s.seat_name,
          seat_id: s.id,
        };
      }).filter((t): t is TicketSelection => t !== null);

      setTicketSelections(calculatedTickets);
    }
  }, [selectedSeats, event?.has_numerated_seats, event?.tickets, allTickets]);

  useEffect(() => {
    const total = ticketSelections.reduce(
      (sum, item) => sum + item.quantity * item.ticket.price,
      0,
    );
    setTotalAmount(total);
  }, [ticketSelections]);

  if (eventLoading || ticketsLoading || businessLoading) {
    return <LoadingLottie />;
  }

  if (!event || !business) {
    return <div>No event or business selected</div>;
  }

  const handleTabChange = (tab: 'sale' | 'report') => {
    setSelectedTab(tab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    }, { replace: true });
  };

  const handleTicketsChange = (selections: TicketSelection[], type: 'entry' | 'extra') => {
    const otherTypeSelections = ticketSelections.filter((item) => (type === 'entry' ? item.ticket.is_extra : !item.ticket.is_extra));
    setTicketSelections([...otherTypeSelections, ...selections]);
  };

  const handleConfirmSale = async () => {
    const selectedTickets = ticketSelections.filter(
      (item) => item.quantity > 0,
    );

    if (selectedTickets.length === 0) {
      return toastError('Selecciona al menos una entrada');
    }

    try {
      dispatch(setTicketsSelected(selectedTickets));

      const orderItemData: OrderItemData[] = [];

      selectedTickets.forEach((selection) => {
        for (
          let i = 0;
          i < selection.quantity * selection.ticket.items_create;
          i += 1
        ) {
          orderItemData.push({
            name: 'Venta',
            last_name: 'Presencial',
            person_id: '0',
            ticket: selection.ticket,
            ticket_id: selection.ticket.id,
            ticket_extra: selection.ticket.is_extra,
            seat_id: selection.seat_id,
          });
        }
      });

      dispatch(setOrderItemData(orderItemData));

      const orderResult = await createOrder({
        order: {
          email: 'ventapresencial@avantti.io',
          items: orderItemData.map((item) => ({
            name: item.name,
            last_name: item.last_name,
            person_id: item.person_id,
            ticket_id: item.ticket.id,
            event_seat_id: item.seat_id,
          })),
          coupon_id: appliedCoupon?.id,
          is_cashier_order: true,
          cashier_id: sessionId,
          payment_method: selectedPaymentMethod,
          is_manual: true,
          from_backoffice: true,
          is_seller_order: false,
          reservation_code: reservation?.code,
          payment_provider: PaymentProvider.AVANTTI,
        },
        businessId: event.business_id,
        eventId: event.id,
      }).unwrap();

      if (orderResult.data) {
        toastSuccess('Venta realizada con éxito');
        navigate(`/admin/order-success?id=${orderResult.data.custom_id}&inperson=true&cashier_id=${cashierId}&session_id=${cashRegister?.cash_register_sessions.find((s) => s.closed_at === null)?.id}`, {
          replace: true,
        });
        return null;
      }
    } catch (error) {
      console.error('Error creating order:', error);
      toastError('Ocurrió un error al procesar la venta');
    }

    return null;
  };

  const handleCancelSale = () => {
    setTicketSelections([]);
  };

  const handleCouponChange = (coupon: CouponDTO | null) => {
    setAppliedCoupon(coupon);
  };

  const salesSummary = [{
    title: 'Venta en efectivo',
    value: formatCurrency(cashierReport?.payment_method_summary[PaymentMethod.CASH] || 0),
  }, {
    title: 'Venta con transferencia',
    value: formatCurrency(cashierReport?.payment_method_summary[PaymentMethod.WIRE_TRANSFER] || 0),
  }, {
    title: 'Venta con Mercado Pago',
    value: formatCurrency(cashierReport?.payment_method_summary[PaymentMethod.MP_POINT] || 0),
  }, {
    title: 'Venta Total caja',
    value: formatCurrency(cashierReport?.total || 0),
  }];

  const cashFromSales = movements
    ?.filter((m) => m.type === 'sale')
    .reduce((sum, m) => sum + m.amount, 0) || 0;
  const cashInAmount = movements
    ?.filter((m) => m.type === 'float_in')
    .reduce((sum, m) => sum + m.amount, 0) || 0;
  const cashOutAmount = movements
    ?.filter((m) => m.type === 'float_out')
    .reduce((sum, m) => sum + m.amount, 0) || 0;
  const cashBalance = cashFromSales + cashInAmount + cashOutAmount;

  const cashSummary = [{
    title: 'Ventas en efectivo',
    value: formatCurrency(cashFromSales),
  }, {
    title: 'Ingresos de efectivo',
    value: formatCurrency(cashInAmount),
  }, {
    title: 'Retiro de efectivo',
    value: formatCurrency(-cashOutAmount),
  }, {
    title: 'Efectivo actual en caja',
    value: formatCurrency(cashBalance),
  }];

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  const handleSelectExpenseType = (type: EventTransactionType) => {
    setExpenseType(type);
  };

  const handleCloseCashRegister = () => {
    setModalType('close');
    setShowModal(true);
  };

  const handleCloseCashRegisterSubmit = () => {
    closeEventCashRegister({
      businessId: event?.business_id || '',
      eventId: event?.id || '',
      cashRegisterId: cashierId || '',
      closingBalance: closingAmount || 0,
    });
    setShowModal(false);
    navigate(`/admin/${event?.business_id}/events/${event?.id}/inperson`);
  };

  const getTotalAmount = () => {
    if (appliedCoupon) {
      return (totalAmount * (appliedCoupon.value)).toLocaleString();
    }
    return totalAmount.toLocaleString();
  };

  return (
    <div className="my-6 flex flex-col gap-8">
      <AdminSectionTitle
        title="Eventos"
        subtitle={`${event?.name} - Caja "${businessCashRegister?.name}"`}
        mainButtons={[
          ...(cashRegister?.status === 'open' && !view ? [{
            label: 'Cerrar caja',
            mobileLabel: 'Cerrar caja',
            onClick: () => handleCloseCashRegister(),
            variant: 'secondary' as const,
            image: '/coin-stack.svg',
          }] : []),
        ]}
      />

      <HorizontalTabs
        tabs={[
          ...(cashRegister?.status === 'open' && !view ? [{ label: 'Venta de entradas', onClick: () => handleTabChange('sale'), selected: selectedTab === 'sale' }] : []),
          { label: 'Reporte de caja', onClick: () => handleTabChange('report'), selected: selectedTab === 'report' },
        ]}
      />

      {selectedTab === 'sale' && cashRegister?.status === 'open' && !view && (
        <>
          {ticketsLoading && <OrdersTableSkeleton />}

          {!ticketsLoading && entryTickets.length === 0 && extraTickets.length === 0 && (
            <div>
              <IconInfoCard
                title="Aún no hay entradas creadas"
                info="Aquí aparecerán todas las entradas creadas para el evento"
                icon={FaLink}
                iconColor="text-indigo-600"
              />
            </div>
          )}

          {!ticketsLoading && (event.has_numerated_seats
            ? (
              <NumeratedSeats
                event={event}
                businessId={event.business_id}
                selectedSeats={selectedSeats}
                onSelectedSeatsChange={setSelectedSeats}
                tickets={eventTickets ?? []}
              />
            )
            : (
              <div>
                {entryTickets.length > 0 && (
                  <div className="mb-6 glass-surface">
                    <h2 className='text-xl font-semibold mb-2 text-text-primary'>
                      Entradas
                    </h2>
                    <SelectTicketsTable
                      tickets={entryTickets}
                      onTicketsChange={(selections) => handleTicketsChange(selections.filter((s) => s.quantity > 0), 'entry')}
                    />
                  </div>
                )}

                {extraTickets.length > 0 && (
                  <div className="glass-surface">
                    <h2 className="text-xl font-semibold mb-2 text-text-primary">
                      Extras
                    </h2>
                    <SelectTicketsTable
                      tickets={extraTickets}
                      onTicketsChange={(selections) => handleTicketsChange(selections.filter((s) => s.quantity > 0), 'extra')}
                    />
                  </div>
                )}
              </div>
            )
          )}
          <div className='w-full'>
            <GotCoupon onCouponChange={handleCouponChange} loadedCode={appliedCoupon?.code || null} />
          </div>
          <div className='flex flex-col md:flex-row gap-8 justify-between items-center w-full'>
            <div className='flex flex-row gap-4 items-center justify-between w-full'>
              <h1 className='text-xl font-semibold w-full'>Medio de pago</h1>
              <Select
                id="payment-method"
                options={[
                  PaymentMethod.CASH,
                  PaymentMethod.WIRE_TRANSFER,
                  PaymentMethod.MP_POINT,
                ].map((method) => ({
                  label: PAYMENT_METHOD_LABELS[method],
                  value: method,
                }))}
                value={selectedPaymentMethod}
                onChange={(e) => setSelectedPaymentMethod(e.target.value as PaymentMethod)}
              />
            </div>
            <div className="flex justify-between items-center glass-surface p-4 w-full text-text-primary">
              <h3 className="text-xl font-bold">Total a cobrar</h3>
              <span className="text-2xl font-bold">
                ${getTotalAmount()}
              </span>
            </div>
          </div>

          <div className='flex flex-col-reverse md:flex-row gap-8 justify-between items-center w-full md:w-1/2 ml-auto'>
            {false && <Button
              variant="secondary"
              onClick={handleCancelSale}
            >
              Cancelar
            </Button>}
            <Button
              variant="primary"
              onClick={handleConfirmSale}
              loading={resultsCreate.isLoading}
            >
              Vender entradas
            </Button>
          </div>

        </>
      )}

      {selectedTab === 'report' && (
        <>
          <div className='flex flex-col md:flex-row gap-4'>
            <div className='w-full md:w-1/2 glass-surface'>
              <h1 className='text-xl font-semibold mb-2'>Reporte de caja</h1>
              <Summary summary={salesSummary} />
            </div>
            <div className='w-full md:w-1/2 glass-surface'>
              <h1 className='text-xl font-semibold mb-2'>Reporte de efectivo</h1>
              <Summary summary={cashSummary} />
            </div>
          </div>

          {!view && <div className="flex flex-col gap-4 glass-surface">
            <h1 className="text-xl font-semibold">Movimientos</h1>
            <CashMovementTable businessId={event?.business_id || ''} eventId={event?.id || ''} />
          </div>}
        </>
      )
      }

      <CustomModal
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        hideCancelButton
      >
        {modalType === 'edit' && (
          <div className='flex flex-col gap-4 md:min-w-[400px]'>
            <div>
              <h1 className='text-lg font-medium'>Editar caja</h1>
              <p className='text-sm text-text-tertiary'>
                Aquí podrás editar los datos de la caja.
              </p>
            </div>
            <CreateEditInPerson handleSubmit={handleSubmit} onCancel={() => setShowModal(false)} />
          </div>
        )}

        {modalType === 'delete' && (
          <div className='flex flex-col gap-4 md:min-w-[400px]'>
            <div>
              <h1 className='text-lg font-medium'>Eliminar caja</h1>
              <p className='text-sm text-text-tertiary'>
                ¿Estás seguro de querer eliminar la caja?
              </p>
            </div>
            <div className='flex flex-row gap-2'>
              <Button variant='secondary' onClick={() => setShowModal(false)}>Cancelar</Button>
              <Button variant='primary' onClick={() => setShowModal(false)}>Eliminar</Button>
            </div>
          </div>
        )}

        {modalType === 'expense' && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <h1 className="text-xl font-medium">Registrar movimiento de caja</h1>
              <p className="text-text-tertiary text-sm">
                Podrás agregar movimientos de caja a esta caja
              </p>
            </div>
            <Select
              id="expense-type"
              label="Tipo de movimiento"
              options={[
                { label: 'Ingreso de dinero', value: EventTransactionType.CashIn },
                { label: 'Egreso de dinero', value: EventTransactionType.CashOut },
              ]}
              value={expenseType}
              onChange={(e) => handleSelectExpenseType(e.target.value as EventTransactionType)}
            />
            <AddUserExpense
              businessId={event?.business_id || ''}
              eventId={event?.id || ''}
              sellerId={cashierId || ''}
              type={expenseType}
              onSuccess={() => setShowModal(false)}
            />
          </div>
        )}

        {modalType === 'close' && (
          <div className='flex flex-col gap-4 md:max-w-[400px]'>
            <h1>Cerrar caja</h1>
            <p>Ingresá la cantidad de efectivo con la que contás al cerrar la caja</p>
            <Input
              id="closing-amount"
              label="Monto final en efectivo"
              labelAbove
              type="number"
              value={closingAmount}
              onChange={(e) => setClosingAmount(e.target.value as unknown as number)}
            />
            <div className='flex flex-row gap-2'>
              <Button variant='secondary' onClick={() => setShowModal(false)}>Cancelar</Button>
              <Button variant='primary' onClick={handleCloseCashRegisterSubmit}>Cerrar</Button>
            </div>
          </div>
        )}
      </CustomModal>

    </div >
  );
};

export default InPersonSalePage;
